import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DollarSign } from "lucide-react";
import { useApi, errMsg, ago } from "@/lib/api";

const cell = "w-20 bg-[#0B101D] border border-white/10 rounded px-2 py-1.5 text-xs font-mono";

export const OddsEntry = ({ meetingId, race, onSaved }) => {
  const api = useApi();
  const [p, setP] = useState({});
  const [busy, setBusy] = useState(false);
  const saved = race.manual_odds;
  useEffect(() => {
    const src = saved?.prices || {};
    setP(Object.fromEntries(race.runners.map((r) => [r.number, { win: String(src[r.number]?.win ?? ""), place: String(src[r.number]?.place ?? "") }])));
  }, [meetingId, race.number, saved?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (n, k, v) => setP({ ...p, [n]: { ...p[n], [k]: v } });

  const save = async () => {
    const prices = {};
    for (const [n, v] of Object.entries(p)) {
      const num = (x) => (x.trim() ? Number(x.trim().replace(",", ".").replace("$", "")) : null);
      const win = num(v.win), place = num(v.place);
      if ([win, place].some((x) => x !== null && (Number.isNaN(x) || x < 1.01 || x > 1000))) { toast.error(`Runner ${n}: enter a price like 4.60 (between 1.01 and 1000)`); return; }
      if (win || place) prices[n] = { win, place };
    }
    setBusy(true);
    try { await api("put", `/admin/meetings/${meetingId}/races/${race.number}/odds`, { prices }); toast.success("Prices saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <section className="rounded-2xl bg-[#131C31] border border-white/10 p-5" data-testid="odds-entry">
      <h3 className="font-display font-bold mb-1 flex items-center gap-2"><DollarSign className="w-4 h-4 text-amber-400" />Enter market prices</h3>
      <p className="text-xs text-slate-500 mb-3">Type win and place prices (e.g. 4.60) once markets are up. Betfair or Krok live odds replace these automatically when available.
        {saved?.updated_at && <span data-testid="odds-entry-updated"> Last saved {ago(saved.updated_at)}.</span>}</p>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mb-4">
        {race.runners.map((r) => (
          <div key={r.number} className={`flex items-center gap-2 text-xs ${r.scratched ? "opacity-40" : ""}`}>
            <span className="flex-1 min-w-0 truncate">{r.number}. {r.horse}{["live", "betfair"].includes(r.odds_source) && <span className="text-emerald-400"> · {r.odds_source === "betfair" ? "Betfair" : "live"} ${r.odds}</span>}</span>
            <input data-testid={`odds-win-${r.number}`} inputMode="decimal" placeholder="Win" value={p[r.number]?.win ?? ""} onChange={(e) => set(r.number, "win", e.target.value)} className={cell} />
            <input data-testid={`odds-place-${r.number}`} inputMode="decimal" placeholder="Place" value={p[r.number]?.place ?? ""} onChange={(e) => set(r.number, "place", e.target.value)} className={cell} />
          </div>
        ))}
      </div>
      <button data-testid="odds-save-button" onClick={save} disabled={busy} className="px-4 py-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-semibold disabled:opacity-40">Save prices</button>
    </section>
  );
};
